import { useEffect } from 'react';
import { CORE_BUSINESS } from '../lib/coreSchemas';
import { injectJsonLd, removeJsonLd } from '../lib/schemaBuilder';

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ MIGRATION NOTICE:
// - useInventoryListSchema → moved to useInventorySchemas.ts
// - useInventoryAggregateOfferSchema → moved to useInventorySchemas.ts
// - useContactPageSchema → moved to SchemaManager.tsx
// - useLocalBusinessContactSchema → moved to SchemaManager.tsx
// ─────────────────────────────────────────────────────────────────────────────

/** Inject Schema.org ItemList structured data for the blog listing page */
export function useBlogListSchema(articles: Array<{
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  heroImage: string;
  date: string;
  author: string;
  category?: string;
  readTime?: string;
  tags?: string[];
}>) {
  useEffect(() => {
    if (!articles || articles.length === 0) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Rockhound Auto Blog — Car Buying Tips & Auto Industry Insights',
      description: 'Expert car buying advice, maintenance tips, and auto industry news from Rockhound Auto Sales. Learn how to make smart vehicle purchasing decisions.',
      url: `${CORE_BUSINESS.url}/blog`,
      numberOfItems: articles.length,
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${CORE_BUSINESS.url}/blog/${article.slug}`,
        item: {
          '@type': 'Article',
          '@id': `${CORE_BUSINESS.url}/blog/${article.slug}`,
          headline: article.title,
          description: article.excerpt,
          image: {
            '@type': 'ImageObject',
            url: article.heroImage,
            width: 1200,
            height: 600,
          },
          datePublished: new Date(article.date).toISOString(),
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: CORE_BUSINESS.name,
            url: CORE_BUSINESS.url,
            logo: {
              '@type': 'ImageObject',
              url: CORE_BUSINESS.logo,
            },
          },
          url: `${CORE_BUSINESS.url}/blog/${article.slug}`,
          ...(article.category ? { articleSection: article.category } : {}),
          ...(article.readTime ? { timeRequired: article.readTime } : {}),
        },
      })),
    };

    injectJsonLd('schema-blog-list', schema);

    return () => removeJsonLd('schema-blog-list');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(articles.map(a => ({ id: a.id, slug: a.slug, title: a.title })))]);
}

/** Inject Schema.org Article structured data for blog posts */
export function useArticleSchema(article: {
  title: string;
  excerpt: string;
  heroImage: string;
  date: string;
  author: string;
  slug: string;
  readTime?: string;
  category?: string;
  tags?: string[];
  updatedAt?: string;
  content?: string;
} | null | undefined) {
  const serialized = article ? JSON.stringify(article) : null;
  useEffect(() => {
    if (!article) return;

    const articleUrl = `${CORE_BUSINESS.url}/blog/${article.slug}`;
    const publishDate = new Date(article.date).toISOString();
    const modifiedDate = article.updatedAt
      ? new Date(article.updatedAt).toISOString()
      : publishDate;

    const calculateWordCount = (): number => {
      if (article.content) {
        const text = article.content.replace(/<[^>]*>/g, ' ');
        const words = text.trim().split(/\s+/).filter(w => w.length > 0);
        return words.length;
      }
      if (!article.readTime) return 800;
      const match = article.readTime.match(/(\d+)/);
      if (!match) return 800;
      return parseInt(match[1], 10) * 175;
    };

    const extractMentions = (): Array<{ '@type': string; name: string }> => {
      const mentions: Array<{ '@type': string; name: string }> = [];
      const vehicleMakes = ['Toyota', 'Ford', 'Honda', 'Subaru', 'Chevrolet', 'Ram', 'Jeep', 'Tesla', 'Nissan', 'Mazda', 'Hyundai', 'Kia'];
      const vehicleModels = ['Tacoma', 'F-150', 'Civic', 'Outback', 'Silverado', 'RAV4', '4Runner', 'CR-V', 'Camry', 'Wrangler', 'Model Y', 'Model 3'];
      const contentText = `${article.title} ${article.excerpt}`.toLowerCase();
      vehicleMakes.forEach(make => {
        if (contentText.includes(make.toLowerCase())) mentions.push({ '@type': 'Product', name: make });
      });
      vehicleModels.forEach(model => {
        if (contentText.includes(model.toLowerCase())) mentions.push({ '@type': 'Product', name: model });
      });
      return mentions.slice(0, 5);
    };

    const buildAbout = (): Array<{ '@type': string; name: string }> => {
      const about: Array<{ '@type': string; name: string }> = [];
      if (article.category) about.push({ '@type': 'Thing', name: article.category });
      if (article.tags && article.tags.length > 0) {
        article.tags.slice(0, 3).forEach(tag => about.push({ '@type': 'Thing', name: tag }));
      }
      return about;
    };

    const wordCount = calculateWordCount();
    const mentions = extractMentions();
    const aboutArray = buildAbout();

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': articleUrl,
      headline: article.title,
      description: article.excerpt,
      image: { '@type': 'ImageObject', url: article.heroImage, width: 1200, height: 600 },
      datePublished: publishDate,
      dateModified: modifiedDate,
      author: { '@type': 'Person', name: article.author, url: CORE_BUSINESS.url },
      publisher: {
        '@type': 'Organization',
        name: CORE_BUSINESS.name,
        url: CORE_BUSINESS.url,
        logo: { '@type': 'ImageObject', url: CORE_BUSINESS.logo, width: 400, height: 120 },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      isPartOf: { '@type': 'WebSite', '@id': `${CORE_BUSINESS.url}/#website`, name: CORE_BUSINESS.name, url: CORE_BUSINESS.url },
      ...(article.readTime ? { timeRequired: article.readTime } : {}),
      ...(article.category ? { articleSection: article.category } : {}),
      wordCount,
      ...(article.tags && article.tags.length > 0 ? { keywords: article.tags.join(', ') } : {}),
      articleBody: article.excerpt,
      inLanguage: 'en-US',
      isAccessibleForFree: true,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.speakable-headline', '.speakable-summary', '.speakable-content'],
      },
      ...(aboutArray.length > 0 ? { about: aboutArray } : {}),
      ...(mentions.length > 0 ? { mentions } : {}),
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: CORE_BUSINESS.url },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${CORE_BUSINESS.url}/blog` },
        { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
      ],
    };

    injectJsonLd('schema-article', articleSchema);
    injectJsonLd('schema-breadcrumb-article', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-article');
      removeJsonLd('schema-breadcrumb-article');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
}

/** Inject Schema.org WebPage + BreadcrumbList for generic pages */
export function useGenericPageSchema(config: {
  type?: string;
  name: string;
  description: string;
  url: string;
  schemaId: string;
  breadcrumbs: Array<{ name: string; item: string }>;
}) {
  const serialized = JSON.stringify(config);
  useEffect(() => {
    if (!config) return;

    const webpage = {
      '@context': 'https://schema.org',
      '@type': config.type || 'WebPage',
      name: config.name,
      description: config.description,
      url: config.url,
      isPartOf: { '@type': 'WebSite', name: CORE_BUSINESS.name, url: CORE_BUSINESS.url },
      publisher: {
        '@type': 'Organization',
        name: CORE_BUSINESS.name,
        logo: { '@type': 'ImageObject', url: CORE_BUSINESS.logo },
      },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.speakable-headline', '.speakable-summary', '.speakable-content'],
      },
      about: {
        '@id': `${CORE_BUSINESS.url}#localbusiness`,
        '@type': 'LocalBusiness',
        name: CORE_BUSINESS.name,
      },
      copyrightYear: '2025',
      inLanguage: 'en-US',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: CORE_BUSINESS.telephone,
        contactType: 'sales',
        areaServed: ['OR', 'WA'],
        availableLanguage: 'English',
      },
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: config.breadcrumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.name,
        item: c.item,
      })),
    };

    injectJsonLd(`schema-${config.schemaId}-webpage`, webpage);
    injectJsonLd(`schema-${config.schemaId}-breadcrumb`, breadcrumb);

    return () => {
      removeJsonLd(`schema-${config.schemaId}-webpage`);
      removeJsonLd(`schema-${config.schemaId}-breadcrumb`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
}

/** Inject Schema.org LocalBusiness for city service area pages */
export function useCityLocalBusinessSchema(city: {
  name: string;
  state: string;
  slug: string;
  distanceMiles: number;
  coordinates: { lat: number; lng: number };
  seo: { title: string; description: string; keywords: string };
}) {
  useEffect(() => {
    if (!city) return;

    const cityUrl = `${CORE_BUSINESS.url}/service-areas/${city.slug}`;
    const stateAbbr = city.state === 'Oregon' ? 'OR' : city.state === 'Washington' ? 'WA' : city.state;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AutomotiveBusiness',
      '@id': `${CORE_BUSINESS.url}#localbusiness-${city.slug}`,
      name: CORE_BUSINESS.name,
      legalName: 'Rockhound Auto LLC',
      description: city.seo.description,
      url: cityUrl,
      telephone: CORE_BUSINESS.telephone,
      email: 'hello@rockhoundauto.com',
      logo: { '@type': 'ImageObject', url: CORE_BUSINESS.logo, width: 400, height: 120 },
      image: CORE_BUSINESS.logo,
      address: {
        '@type': 'PostalAddress',
        streetAddress: CORE_BUSINESS.address.streetAddress,
        addressLocality: CORE_BUSINESS.address.addressLocality,
        addressRegion: CORE_BUSINESS.address.addressRegion,
        postalCode: CORE_BUSINESS.address.postalCode,
        addressCountry: CORE_BUSINESS.address.addressCountry,
      },
      geo: { '@type': 'GeoCoordinates', latitude: CORE_BUSINESS.geo.latitude, longitude: CORE_BUSINESS.geo.longitude },
      hasMap: `https://www.google.com/maps?q=${city.coordinates.lat},${city.coordinates.lng}`,
      tourBookingPage: `${cityUrl}#contact`,
      amenityFeature: [
        { '@type': 'LocationFeatureSpecification', name: 'Free Vehicle Delivery', value: true },
        { '@type': 'LocationFeatureSpecification', name: 'No-Pressure Sales', value: true },
        { '@type': 'LocationFeatureSpecification', name: 'Transparent Pricing', value: true },
        { '@type': 'LocationFeatureSpecification', name: 'Financing Available', value: true },
        { '@type': 'LocationFeatureSpecification', name: 'Trade-In Accepted', value: true },
        { '@type': 'LocationFeatureSpecification', name: 'Test Drives Available', value: true },
      ],
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '16:00' },
      ],
      priceRange: '$$',
      currenciesAccepted: 'USD',
      paymentAccepted: 'Cash, Credit Card, Financing',
      areaServed: [
        { '@type': 'City', name: city.name, containedInPlace: { '@type': 'State', name: city.state } },
        { '@type': 'City', name: 'Warrenton', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'State', name: 'Oregon' },
        { '@type': 'State', name: 'Washington' },
      ],
      serviceArea: {
        '@type': 'GeoCircle',
        geoMidpoint: { '@type': 'GeoCoordinates', latitude: city.coordinates.lat, longitude: city.coordinates.lng },
        geoRadius: city.distanceMiles > 0 ? city.distanceMiles * 1609 : 50000,
      },
      sameAs: [
        'https://www.google.com/maps/place/Rockhound+Auto',
        'https://www.facebook.com/rockhoundauto',
        'https://www.yelp.com/biz/rockhound-auto-warrenton',
        'https://www.instagram.com/rockhoundauto',
        'https://www.youtube.com/@rockhoundauto',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: CORE_BUSINESS.telephone,
        contactType: 'customer service',
        areaServed: [stateAbbr, 'OR', 'WA'],
        availableLanguage: 'English',
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      },
      makesOffer: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: `Used Car Sales serving ${city.name}, ${stateAbbr}`, description: `Quality pre-owned vehicles delivered to ${city.name} and surrounding areas. Transparent pricing, no-pressure sales.` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Auto Broker Services', description: 'We source, negotiate, and deliver any vehicle from any dealership in the Pacific Northwest.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Auto Financing', description: 'Flexible financing for all credit situations. Pre-approval in minutes.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vehicle Trade-In', description: 'Fair market value trade-in appraisals for your current vehicle.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: `Free Vehicle Delivery to ${city.name}`, description: `We deliver purchased vehicles directly to customers in ${city.name}, ${stateAbbr} at no extra charge.` } },
      ],
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: CORE_BUSINESS.url },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${CORE_BUSINESS.url}/service-areas` },
        { '@type': 'ListItem', position: 3, name: `${city.name}, ${stateAbbr}`, item: cityUrl },
      ],
    };

    const webpage = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: city.seo.title,
      description: city.seo.description,
      url: cityUrl,
      keywords: city.seo.keywords,
      isPartOf: { '@type': 'WebSite', name: CORE_BUSINESS.name, url: CORE_BUSINESS.url },
      about: {
        '@type': 'AutomotiveBusiness',
        '@id': `${CORE_BUSINESS.url}#localbusiness-${city.slug}`,
        name: CORE_BUSINESS.name,
      },
      publisher: {
        '@type': 'Organization',
        name: CORE_BUSINESS.name,
        logo: { '@type': 'ImageObject', url: CORE_BUSINESS.logo },
      },
    };

    injectJsonLd(`schema-city-localbusiness-${city.slug}`, schema);
    injectJsonLd(`schema-city-breadcrumb-${city.slug}`, breadcrumb);
    injectJsonLd(`schema-city-webpage-${city.slug}`, webpage);

    return () => {
      removeJsonLd(`schema-city-localbusiness-${city.slug}`);
      removeJsonLd(`schema-city-breadcrumb-${city.slug}`);
      removeJsonLd(`schema-city-webpage-${city.slug}`);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(city)]);
}

/** Inject Schema.org PriceSpecification for a Deal Radar price-drop card */
export function useDealRadarPriceSchema(
  deals: Array<{
    id: string;
    year: number;
    make: string;
    model: string;
    price: number;
    priceDropAmount?: number;
    images: string[];
    dealTemperature: 'hot' | 'trending' | 'cool';
  }>
) {
  useEffect(() => {
    if (!deals || deals.length === 0) return;

    const priceDropDeals = deals.filter((d) => d.priceDropAmount && d.priceDropAmount > 0);
    if (priceDropDeals.length === 0) return;

    const validFrom = new Date().toISOString().split('T')[0];

    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Rockhound Auto — Current Price Drop Deals',
      description: 'Live price drop deals on used cars at Rockhound Auto in Warrenton, OR. Updated in real time.',
      url: `${CORE_BUSINESS.url}/deal-radar`,
      numberOfItems: priceDropDeals.length,
      itemListElement: priceDropDeals.slice(0, 20).map((deal, i) => {
        const vehicleUrl = `${CORE_BUSINESS.url}/inventory/${deal.id}`;
        const vehicleName = `${deal.year} ${deal.make} ${deal.model}`;
        const originalPrice = deal.price + (deal.priceDropAmount || 0);
        const priceValidUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0];

        return {
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Car',
            name: vehicleName,
            url: vehicleUrl,
            ...(deal.images[0] ? { image: deal.images[0] } : {}),
            brand: { '@type': 'Brand', name: deal.make },
            model: deal.model,
            vehicleModelDate: String(deal.year),
            itemCondition: 'https://schema.org/UsedCondition',
            offers: {
              '@type': 'Offer',
              url: vehicleUrl,
              priceCurrency: 'USD',
              price: deal.price,
              validFrom,
              priceValidUntil,
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/UsedCondition',
              priceSpecification: [
                {
                  '@type': 'PriceSpecification',
                  price: deal.price,
                  priceCurrency: 'USD',
                  description: `Current sale price — reduced by $${(deal.priceDropAmount || 0).toLocaleString()}`,
                  valueAddedTaxIncluded: false,
                },
                {
                  '@type': 'PriceSpecification',
                  price: originalPrice,
                  priceCurrency: 'USD',
                  description: 'Original list price before price drop',
                  valueAddedTaxIncluded: false,
                },
              ],
              seller: {
                '@type': 'AutoDealer',
                name: CORE_BUSINESS.name,
                url: CORE_BUSINESS.url,
                telephone: CORE_BUSINESS.telephone,
              },
            },
          },
        };
      }),
    };

    injectJsonLd('schema-deal-radar-price-spec', itemList);
    return () => removeJsonLd('schema-deal-radar-price-spec');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(deals.map((d) => ({ id: d.id, price: d.price, priceDropAmount: d.priceDropAmount })))]);
}

/** Inject Schema.org AggregateOffer for home page with live inventory count and price range */
export function useHomeAggregateOfferSchema(vehicles: Array<{
  id: number;
  uuid: string;
  year: number;
  make: string;
  model: string;
  price: number;
  mileage: number;
  status: string;
  vin?: string;
}>, averagePrice?: number) {
  useEffect(() => {
    if (!vehicles || vehicles.length === 0) return;

    const validFrom = new Date().toISOString().split('T')[0];
    const priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0];

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AggregateOffer',
      '@id': `${CORE_BUSINESS.url}/#aggregate-offer`,
      name: 'Rockhound Auto Inventory',
      description: 'Browse quality pre-owned vehicles at Rockhound Auto in Warrenton, Oregon. Serving Astoria, Seaside, Tillamook, and the Pacific Northwest. Transparent pricing, no hidden fees.',
      url: `${CORE_BUSINESS.url}/inventory`,
      validFrom,
      priceValidUntil,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/UsedCondition',
      ...(averagePrice ? { lowPrice: String(averagePrice), highPrice: String(averagePrice) } : {}),
      offers: vehicles.slice(0, 20).map((v) => ({
        '@type': 'Offer',
        price: String(v.price),
        priceCurrency: 'USD',
        validFrom,
        priceValidUntil,
        itemCondition: 'https://schema.org/UsedCondition',
        availability:
          v.status === 'available'
            ? 'https://schema.org/InStock'
            : 'https://schema.org/SoldOut',
        seller: {
          '@type': 'AutoDealer',
          name: CORE_BUSINESS.name,
          url: CORE_BUSINESS.url,
          telephone: CORE_BUSINESS.telephone,
          address: {
            '@type': 'PostalAddress',
            streetAddress: CORE_BUSINESS.address.streetAddress,
            addressLocality: CORE_BUSINESS.address.addressLocality,
            addressRegion: CORE_BUSINESS.address.addressRegion,
            postalCode: CORE_BUSINESS.address.postalCode,
            addressCountry: CORE_BUSINESS.address.addressCountry,
          },
        },
      })),
    };

    injectJsonLd('schema-home-aggregate-offer', schema);
    return () => removeJsonLd('schema-home-aggregate-offer');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(vehicles.map((v) => ({ id: v.id, price: v.price, status: v.status })))]);
}